/**
 * CommitPreviewTree - Visual tree for save confirmation
 * 
 * Shows a collapsible tree view of items to be saved with visual indicators:
 * - 🆕 NEW items (green) - will be added
 * - ♻️ EXISTING items (blue) - already in your Life Map
 * - 🚫 SKIPPED items (red, strikethrough) - will not be included
 */

import React, { useState } from 'react'
import { 
 ChevronRight, ChevronDown, Star, Target, CheckSquare, 
 ListTodo, CheckCircle2, AlertTriangle, BookOpen, Plus, RefreshCw, XCircle
} from 'lucide-react'

// Item status in the commit preview
export type CommitItemStatus = 'new' | 'existing' | 'rejected'

export interface CommitPreviewItem {
 title: string
 type: string
 status: CommitItemStatus
 children?: CommitPreviewItem[]
 priority?: string | null
 executionOrder?: number | null
}

interface CommitPreviewTreeProps {
 items: CommitPreviewItem[]
 maxHeight?: string
 showCounts?: boolean
}

// Icons for each type
const typeIcons: Record<string, React.ReactNode> = {
 dream: <Star className="w-4 h-4 text-warn" />,
 aspiration: <Target className="w-4 h-4 text-info" />,
 goal: <CheckSquare className="w-4 h-4 text-brand" />,
 task: <ListTodo className="w-4 h-4 text-ok" />,
 action_item: <CheckCircle2 className="w-4 h-4 text-ok" />,
 roadblock: <AlertTriangle className="w-4 h-4 text-warn" />,
 knowledge_skill: <BookOpen className="w-4 h-4 text-info" />,
}

// Status indicators
const statusConfig: Record<CommitItemStatus, { icon: React.ReactNode; label: string; className: string }> = {
 new: {
 icon: <Plus className="w-3 h-3" />,
 label: 'New',
 className: 'bg-tint-ok-fill text-ok border-tint-ok-edge',
 },
 existing: {
 icon: <RefreshCw className="w-3 h-3" />,
 label: 'Saved',
 className: 'bg-brand-subtle text-brand border-brand-subtle',
 },
 rejected: {
 icon: <XCircle className="w-3 h-3" />,
 label: 'Skipped',
 className: 'bg-surface-alt /30 text-muted border-soft ',
 },
}

function TreeNode({ item, level = 0 }: { item: CommitPreviewItem; level?: number }) {
 const [isExpanded, setIsExpanded] = useState(true)
 const hasChildren = item.children && item.children.length > 0
 const statusCfg = statusConfig[item.status]
 const typeIcon = typeIcons[item.type] || <CheckSquare className="w-4 h-4 text-muted" />
 
 return (
 <div>
 {/* Node row */}
 <div 
 className={`
 flex items-center gap-2 py-1.5 px-2 rounded-lg
 ${level > 0 ? 'ml-4' : ''}
 ${item.status === 'rejected' ? 'opacity-60' : ''}
 hover:bg-surface-alt/50 transition-colors
 `}
 style={{ marginLeft: level * 16 }}
 >
 {/* Expand/collapse button */}
 {hasChildren ? (
 <button
 onClick={() => setIsExpanded(!isExpanded)}
 className="p-0.5 hover:bg-surface-alt rounded transition-colors"
 >
 {isExpanded 
 ? <ChevronDown className="w-3.5 h-3.5 text-muted" />
 : <ChevronRight className="w-3.5 h-3.5 text-muted" />
 }
 </button>
 ) : (
 <div className="w-4" /> // Spacer
 )}
 
 {/* Execution order badge (for tasks) */}
 {item.executionOrder && (
 <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-brand-subtle text-brand text-xs font-bold">
 {item.executionOrder}
 </span>
 )}
 
 {/* Type icon */}
 <div className="flex-shrink-0">{typeIcon}</div>
 
 {/* Title */}
 <span className={`
 flex-1 text-sm truncate
 ${item.status === 'rejected' ? 'line-through text-muted' : 'text-primary'}
 `}>
 {item.title}
 </span>
 
 {/* Priority badge (for tasks/goals) */}
 {item.priority && item.priority !== 'null' && (
 <span className={`
 flex-shrink-0 px-1.5 py-0.5 text-xs font-medium rounded
 ${item.priority === 'critical' || item.priority === 'high' 
 ? 'bg-tint-bad-fill text-bad' 
 : item.priority === 'medium'
 ? 'bg-tint-warn-fill text-warn'
 : 'bg-surface-alt text-secondary'}
 `}>
 {item.priority}
 </span>
 )}
 
 {/* Status badge */}
 <span className={`
 flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded border
 ${statusCfg.className}
 `}>
 {statusCfg.icon}
 <span className="hidden sm:inline">{statusCfg.label}</span>
 </span>
 </div>
 
 {/* Children */}
 {hasChildren && isExpanded && (
 <div className="border-l-2 border-soft ml-4">
 {item.children!.map((child, idx) => (
 <TreeNode key={`${child.type}-${child.title}-${idx}`} item={child} level={level + 1} />
 ))}
 </div>
 )}
 </div>
 )
}

export function CommitPreviewTree({ items, maxHeight = '300px', showCounts = true }: CommitPreviewTreeProps) {
 // Count items by status
 const countItems = (items: CommitPreviewItem[]): { new: number; existing: number; rejected: number } => {
 let counts = { new: 0, existing: 0, rejected: 0 }
 
 const traverse = (items: CommitPreviewItem[]) => {
 for (const item of items) {
 counts[item.status]++
 if (item.children) {
 traverse(item.children)
 }
 }
 }
 
 traverse(items)
 return counts
 }
 
 const counts = countItems(items)
 const totalToCommit = counts.new + counts.existing
 
 if (items.length === 0) {
 return (
 <div className="text-center py-6 text-muted">
 <CheckSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
 <p className="text-sm">No items to preview</p>
 </div>
 )
 }
 
 return (
 <div className="space-y-3">
 {/* Summary counts */}
 {showCounts && (
 <div className="flex flex-wrap gap-2 pb-2 border-b border-soft">
 {counts.new > 0 && (
 <div className="flex items-center gap-1.5 px-2 py-1 bg-tint-ok-fill text-ok rounded-full text-xs font-medium">
 <Plus className="w-3 h-3" />
 {counts.new} new
 </div>
 )}
 {counts.existing > 0 && (
 <div className="flex items-center gap-1.5 px-2 py-1 bg-brand-subtle text-brand rounded-full text-xs font-medium">
 <RefreshCw className="w-3 h-3" />
 {counts.existing} already saved
 </div>
 )}
 {counts.rejected > 0 && (
 <div className="flex items-center gap-1.5 px-2 py-1 bg-surface-alt /30 text-muted rounded-full text-xs font-medium">
 <XCircle className="w-3 h-3" />
 {counts.rejected} skipped
 </div>
 )}
 </div>
 )}
 
 {/* Scrollable tree */}
 <div 
 className="overflow-y-auto overflow-x-hidden pr-2 -mr-2"
 style={{ maxHeight }}
 >
 <div className="space-y-0.5">
 {items.map((item, idx) => (
 <TreeNode key={`${item.type}-${item.title}-${idx}`} item={item} />
 ))}
 </div>
 </div>
 
 {/* Footer summary */}
 <div className="pt-2 border-t border-soft text-xs text-muted text-center">
 {totalToCommit} item{totalToCommit !== 1 ? 's' : ''} will be added to your Life Map
 {counts.rejected > 0 && ` (${counts.rejected} skipped)`}
 </div>
 </div>
 )
}

/**
 * Convert V3 hierarchy items to CommitPreviewItem format
 */
export function convertToPreviewItems(
 items: any[],
 itemStates: Map<string, any>,
 existingItemTitles: Set<string>
): CommitPreviewItem[] {
 console.log('🌳 convertToPreviewItems called with:', items.length, 'items')
 
 const convertItem = (item: any, itemType: string, depth: number = 0): CommitPreviewItem => {
 const key = `${itemType}:${item.title}`
 const state = itemStates.get(key)
 const indent = ' '.repeat(depth)
 
 console.log(`${indent}📦 Converting: ${key}`)
 
 // Determine status
 let status: CommitItemStatus = 'new'
 if (state?.status === 'rejected') {
 status = 'rejected'
 } else if (existingItemTitles.has(key)) {
 status = 'existing'
 }
 
 // Build children
 const children: CommitPreviewItem[] = []
 
 // Check various child arrays - handle both flat and nested structures
 const childMappings: [string, string][] = [
 ['aspirations', 'aspiration'],
 ['goals', 'goal'],
 // The engine emits `skill` (not `knowledge_skill`); without this mapping
 // skill children were dropped, so a goal with only skills looked childless
 // and the whole tree collapsed to the flat grouped view.
 ['skills', 'skill'],
 ['tasks', 'task'],
 ['action_items', 'action_item'],
 ['roadblocks', 'roadblock'],
 ['habits', 'habit'],
 ['knowledge_skills', 'knowledge_skill'],
 ]
 
 for (const [arrayName, childType] of childMappings) {
 // Try multiple possible locations for children
 const childArray = 
 item[arrayName] || // Direct array (e.g., item.aspirations)
 item.children?.[childType] || // Children object with singular
 item.children?.[arrayName] || // Children object with plural
 item.children?.[childType.replace('_', '')] || // Without underscore
 []
 
 if (Array.isArray(childArray) && childArray.length > 0) {
 console.log(`${indent} 📁 Found ${childArray.length} ${arrayName}`)
 for (const child of childArray) {
 children.push(convertItem(child, childType, depth + 1))
 }
 }
 }
 
 // Also check fields.action_items (special case for tasks)
 const hasActionItemsAlready = children.some(c => c.type === 'action_item')
 if (!hasActionItemsAlready && item.fields?.action_items && Array.isArray(item.fields.action_items)) {
 console.log(`${indent} 📁 Found ${item.fields.action_items.length} action_items in fields`)
 for (const ai of item.fields.action_items) {
 children.push(convertItem(ai, 'action_item', depth + 1))
 }
 }
 
 return {
 title: state?.editedFields?.title || item.title,
 type: itemType,
 status,
 children: children.length > 0 ? children : undefined,
 priority: item.fields?.priority || item.priority || null,
 executionOrder: item.fields?.execution_order || item.execution_order || null,
 }
 }
 
 // If items is empty but we received a flat structure, group them
 if (items.length === 0) {
 console.log('⚠️ No items to convert')
 return []
 }
 
 const result = items.map(item => convertItem(item, item.type || 'goal', 0))
 console.log('🌳 Converted', result.length, 'root items with total structure:', JSON.stringify(result, null, 2).slice(0, 500))
 return result
}

/**
 * Alternative: Convert flat items list to grouped preview (for when hierarchy isn't available)
 */
export function convertFlatItemsToPreview(
 flatItems: any[],
 itemStates: Map<string, any>,
 existingItemTitles: Set<string>
): CommitPreviewItem[] {
 console.log('📋 convertFlatItemsToPreview called with:', flatItems.length, 'items')
 
 // Group items by type
 const grouped: Record<string, CommitPreviewItem[]> = {
 dream: [],
 aspiration: [],
 goal: [],
 task: [],
 action_item: []
 }
 
 for (const item of flatItems) {
 const itemType = item.type || 'goal'
 const key = `${itemType}:${item.title}`
 const state = itemStates.get(key)
 
 let status: CommitItemStatus = 'new'
 if (state?.status === 'rejected') {
 status = 'rejected'
 } else if (existingItemTitles.has(key)) {
 status = 'existing'
 }
 
 const previewItem: CommitPreviewItem = {
 title: state?.editedFields?.title || item.title,
 type: itemType,
 status,
 priority: item.fields?.priority || item.priority || null,
 }
 
 if (grouped[itemType]) {
 grouped[itemType].push(previewItem)
 }
 
 // Also extract action_items from fields
 if (item.fields?.action_items && Array.isArray(item.fields.action_items)) {
 for (const ai of item.fields.action_items) {
 const aiKey = `action_item:${ai.title}`
 const aiState = itemStates.get(aiKey)
 
 let aiStatus: CommitItemStatus = 'new'
 if (aiState?.status === 'rejected') {
 aiStatus = 'rejected'
 } else if (existingItemTitles.has(aiKey)) {
 aiStatus = 'existing'
 }
 
 grouped.action_item.push({
 title: aiState?.editedFields?.title || ai.title,
 type: 'action_item',
 status: aiStatus,
 })
 }
 }
 }
 
 // Create category wrappers
 const result: CommitPreviewItem[] = []
 const typeConfig: Record<string, {label: string}> = {
 dream: { label: '🌟 Dreams' },
 aspiration: { label: '🎯 Aspirations' },
 goal: { label: '📊 Goals' },
 task: { label: '📋 Tasks' },
 action_item: { label: '✓ Action Items' }
 }
 
 for (const [type, items] of Object.entries(grouped)) {
 if (items.length > 0) {
 result.push({
 title: `${typeConfig[type]?.label || type} (${items.length})`,
 type: type,
 status: 'new', // Category headers are always"new"
 children: items
 })
 }
 }
 
 return result
}

